import type {Line, Object3D} from "three";
import {
	Group,
	Mesh,
	MeshBasicMaterial,
	Ray,
	Vector2,
	Vector3,
	Box3,
	DoubleSide,
	BufferGeometry,
	BufferAttribute,
	CircleGeometry,
	Shape,
	ExtrudeGeometry,
} from "three";
import {Line2, LineGeometry, LineMaterial} from "three/examples/jsm/Addons.js";
import {THREEUtils} from "../../../../../../utils/THREEUtils";
import type {SpaceViewRenderer} from "../renderers/SpaceViewRenderer";
import type {SpaceItemType} from "../managers/spaceitems/ItemManager";
import type {IModel} from "../../../../../../data/models/Model";
import type {Dimensions, LinkPropertyDto, PointDouble} from "../../../../../../generated/api/base";
import {ArrowStyle, LineStyle} from "../../../../../../generated/api/base";
import {LinkDirection} from "../../../../../../generated/api/base/models/LinkDirection";
import type {IBox2} from "../../../../../../utils/THREEUtils";
import type {Link} from "../../../../../../data/models/Link";
import {Constants} from "../Constants";
import type {Xyicon3D} from "./Xyicon3D";
import {calculateArrowHead} from "./markups/abstract/MarkupUtils";
import {SpaceItem} from "./SpaceItem";

interface ArrowHeadInfo {
	arrowHead: Mesh | Line | undefined;
	tipPosition: Vector3 | undefined;
	isAtoB: boolean;
	propertyKey: "_arrowHead1" | "_arrowHead2";
	selectionBoxKey: "_selectionBox1" | "_selectionBox2";
}

export class LinkLineArrow extends SpaceItem {
	private _line: Line2;
	private _arrowHead1?: Mesh | Line;
	private _arrowHead2?: Mesh | Line;
	private _selectionBox1?: Group;
	private _selectionBox2?: Group;
	private _arrowTip1Position?: Vector3;
	private _arrowTip2Position?: Vector3;
	private _originalIntersectionA?: Vector3;
	private _originalIntersectionB?: Vector3;
	private _material: LineMaterial;
	private _lineGeometry: LineGeometry;
	private _parent: Group;
	private _linkProperties: LinkPropertyDto;
	private _isHovered: boolean = false;
	private _lineColor: number;
	private _floorContainer: Group;
	private _fromXyicon: Xyicon3D;
	private _toXyicon: Xyicon3D;
	private _link: Link;
	protected _modelData: IModel = null;
	protected _spaceItemType: SpaceItemType = "link";

	// Constants
	private static readonly ARROW_HEAD_WIDTH = 20;
	private static readonly ARROW_HEAD_HEIGHT = 24;
	private static readonly SELECTION_BOX_SIZE = 10;
	private static readonly INNER_BOX_SIZE = 6;
	private static readonly SELECTION_BOX_RADIUS = 2;
	private static readonly DASH_SIZE = 20;
	private static readonly GAP_SIZE = 20;

	// Dynamic getters with correction multiplier
	private get ARROW_HEAD_WIDTH(): number {
		return LinkLineArrow.ARROW_HEAD_WIDTH * this.spaceViewRenderer.correctionMultiplier.current;
	}
	private get ARROW_HEAD_HEIGHT(): number {
		return LinkLineArrow.ARROW_HEAD_HEIGHT * this.spaceViewRenderer.correctionMultiplier.current;
	}
	private get SELECTION_BOX_SIZE(): number {
		return LinkLineArrow.SELECTION_BOX_SIZE * this.spaceViewRenderer.correctionMultiplier.current;
	}
	private get INNER_BOX_SIZE(): number {
		return LinkLineArrow.INNER_BOX_SIZE * this.spaceViewRenderer.correctionMultiplier.current;
	}
	private get SELECTION_BOX_RADIUS(): number {
		return LinkLineArrow.SELECTION_BOX_RADIUS * this.spaceViewRenderer.correctionMultiplier.current;
	}
	private get DASH_SIZE(): number {
		return LinkLineArrow.DASH_SIZE * this.spaceViewRenderer.correctionMultiplier.current;
	}
	private get GAP_SIZE(): number {
		return LinkLineArrow.GAP_SIZE * this.spaceViewRenderer.correctionMultiplier.current;
	}
	private get LINE_WIDTH(): number {
		return this._linkProperties.settings.lineWidth;
	}
	protected get _color(): number {
		return this._lineColor;
	}

	constructor(
		fromObjectA: Xyicon3D,
		fromObjectB: Xyicon3D,
		parent: Group,
		spaceViewRenderer: SpaceViewRenderer,
		link: Link,
		linkProperties: LinkPropertyDto = {},
	) {
		super(spaceViewRenderer);
		this._parent = parent;
		this._linkProperties = this.createDefaultLinkProperties(linkProperties);
		this._lineColor = this.getLineColorAsNumber(this._linkProperties.settings.lineColor);
		this._link = link;
		this._isSelected = link.isSelected;
		this.addModelData(link);

		const {objectA, objectB} = this.mapXyiconsToObjects(fromObjectA, fromObjectB, link);
		this._fromXyicon = objectA;
		this._toXyicon = objectB;

		this._floorContainer = this.setupFloorContainer();
		const {start, end} = this.calculateIntersectionPoints(objectA, objectB);
		const floorPoints = this.setupIntersectionPoints(start, end, this._floorContainer);

		this.initializeLine();
		this.updateLine(floorPoints.start, floorPoints.end);
		THREEUtils.add(this._floorContainer, this._line);
		THREEUtils.updateMatrices(this._floorContainer);

		if (this.shouldCreateArrowHeads()) {
			this.createInitialArrowHeads();
		}
		this.updateArrowHeads(floorPoints.start, floorPoints.end);
	}

	// Utility methods
	private getLineColorAsNumber(hexString: string): number {
		return parseInt(hexString.replace("#", ""), 16);
	}

	private createDefaultLinkProperties(linkProperties: LinkPropertyDto = {}): LinkPropertyDto {
		return {
			direction: linkProperties?.direction ?? LinkDirection.Both,
			settings: {
				lineColor: linkProperties?.settings?.lineColor ?? "#1E88E5",
				lineWidth: linkProperties?.settings?.lineWidth ?? 4,
				arrowStyle: linkProperties?.settings?.arrowStyle ?? ArrowStyle.Filled,
				lineStyle: linkProperties?.settings?.lineStyle ?? LineStyle.Solid,
			},
		};
	}

	private mapXyiconsToObjects(fromObjectA: Xyicon3D, fromObjectB: Xyicon3D, link: Link): {objectA: Xyicon3D; objectB: Xyicon3D} {
		const isFromObjectAObjectA = fromObjectA.id === link.objectAID && fromObjectB.id === link.objectBID;
		const isFromObjectAObjectB = fromObjectA.id === link.objectBID && fromObjectB.id === link.objectAID;
		return {
			objectA: isFromObjectAObjectA ? fromObjectA : isFromObjectAObjectB ? fromObjectB : fromObjectA,
			objectB: isFromObjectAObjectA ? fromObjectB : isFromObjectAObjectB ? fromObjectA : fromObjectB,
		};
	}

	private calculateActualBoundingBox(obj: Xyicon3D): Box3 {
		const {min, max} = obj.boundingBox;
		return new Box3(new Vector3(min.x, min.y, 0), new Vector3(max.x, max.y, 0));
	}

	private calculateIntersectionPoints(objectA: Xyicon3D, objectB: Xyicon3D): {start: Vector3; end: Vector3} {
		const worldPositionA = new Vector3();
		const worldPositionB = new Vector3();
		objectA.group.getWorldPosition(worldPositionA);
		objectB.group.getWorldPosition(worldPositionB);

		const boxA = this.calculateActualBoundingBox(objectA);
		const boxB = this.calculateActualBoundingBox(objectB);

		boxA.getCenter(worldPositionA);
		boxB.getCenter(worldPositionB);

		const directionAToB = new Vector3(
			worldPositionB.x - worldPositionA.x,
			worldPositionB.y - worldPositionA.y,
			worldPositionB.z - worldPositionA.z,
		).normalize();

		const intersectionA = new Ray(worldPositionA, directionAToB).intersectBox(boxA, new Vector3());
		const intersectionB = new Ray(worldPositionB, directionAToB.clone().multiplyScalar(-1)).intersectBox(boxB, new Vector3());

		return {
			start: intersectionA || worldPositionA.clone(),
			end: intersectionB || worldPositionB.clone(),
		};
	}

	private setupFloorContainer(): Group {
		const floorContainer = new Group();
		floorContainer.name = "arrowLines";
		(this._parent.parent || this._parent).add(floorContainer);
		floorContainer.position.set(this._parent.position.x, this._parent.position.y, 0);
		return floorContainer;
	}

	private setupIntersectionPoints(start: Vector3, end: Vector3, floorContainer: Group): {start: Vector3; end: Vector3} {
		this._originalIntersectionA = start.clone().setZ(0);
		this._originalIntersectionB = end.clone().setZ(0);
		floorContainer.worldToLocal(this._originalIntersectionA);
		floorContainer.worldToLocal(this._originalIntersectionB);

		const floorStart = start.clone().setZ(0);
		const floorEnd = end.clone().setZ(0);
		floorContainer.worldToLocal(floorStart);
		floorContainer.worldToLocal(floorEnd);

		return {start: floorStart, end: floorEnd};
	}

	private initializeLine(): void {
		this._material = new LineMaterial({
			color: this._lineColor,
			linewidth: this.LINE_WIDTH,
			resolution: new Vector2(this._spaceViewRenderer.canvas.width, this._spaceViewRenderer.canvas.height),
		});

		this.applyDashedLineStyle();
		this._lineGeometry = new LineGeometry();
		this._line = new Line2(this._lineGeometry, this._material);
		this._line.name = "linkLine";
	}

	private applyDashedLineStyle(): void {
		const isDashed = this._linkProperties.settings.lineStyle === LineStyle.Dashed;
		this._material.dashed = isDashed;

		if (isDashed) {
			this._material.dashSize = this.DASH_SIZE;
			this._material.gapSize = this.GAP_SIZE;
			this._material.defines.USE_DASH = "";
		} else {
			delete this._material.defines.USE_DASH;
		}

		this._material.needsUpdate = true;
		if (this._line) {
			this._line.computeLineDistances();
		}
	}

	private shouldCreateArrowHeads(): boolean {
		return [ArrowStyle.Arrow, ArrowStyle.Filled, ArrowStyle.Dotted].includes(this._linkProperties.settings.arrowStyle);
	}

	private createBasicMeshArrowHead(): Mesh {
		const material = new MeshBasicMaterial({color: this._lineColor, side: DoubleSide});
		const mesh = new Mesh(new BufferGeometry(), material);
		mesh.name = "linkLine";
		return mesh;
	}

	private createArrowLineMaterial(): LineMaterial {
		const material = new LineMaterial({
			color: this._lineColor,
			linewidth: this._linkProperties.settings.lineWidth,
			resolution: new Vector2(this._spaceViewRenderer.canvas.width, this._spaceViewRenderer.canvas.height),
		});

		if (this._material.dashed) {
			material.dashed = true;
			material.dashSize = this.DASH_SIZE;
			material.gapSize = this.GAP_SIZE;
			material.defines.USE_DASH = "";
			material.needsUpdate = true;
		}
		return material;
	}

	private createInitialArrowHeads(): void {
		const directions = [
			{
				condition: this._linkProperties.direction === LinkDirection.AtoB || this._linkProperties.direction === LinkDirection.Both,
				key: "_arrowHead1" as const,
			},
			{
				condition: this._linkProperties.direction === LinkDirection.BtoA || this._linkProperties.direction === LinkDirection.Both,
				key: "_arrowHead2" as const,
			},
		];

		directions.forEach(({condition, key}) => {
			if (condition) {
				this[key] = this.createBasicMeshArrowHead();
				THREEUtils.add(this._floorContainer, this[key]!);
			}
		});
	}

	private updateLine(start: Vector3, end: Vector3): void {
		start.z = end.z = 0;
		const {adjustedStart, adjustedEnd} = this.calculateAdjustedLineEndpoints(start, end);

		this._lineGeometry.setPositions([adjustedStart.x, adjustedStart.y, adjustedStart.z, adjustedEnd.x, adjustedEnd.y, adjustedEnd.z]);

		if (this._material.dashed) {
			this._line.computeLineDistances();
		}
		if (this._isSelected) {
			this.showSelectionBoxes();
		}
		this._spaceViewRenderer.needsRender = true;
	}

	private calculateAdjustedLineEndpoints(start: Vector3, end: Vector3): {adjustedStart: Vector3; adjustedEnd: Vector3} {
		if (this._linkProperties.direction === LinkDirection.None) {
			return {adjustedStart: start.clone(), adjustedEnd: end.clone()};
		}

		const directionVector = new Vector3().subVectors(end, start).normalize();

		// Base adjustment distance
		let adjustmentDistance =
			this._linkProperties.settings.arrowStyle === ArrowStyle.Dotted ? this.ARROW_HEAD_HEIGHT / 2 : this.ARROW_HEAD_HEIGHT * 0.1;

		// For filled arrows, add extra adjustment based on line width to prevent line showing beyond arrow tip
		if (this._linkProperties.settings.arrowStyle === ArrowStyle.Filled) {
			// Additional adjustment: half the line width scaled by correction multiplier
			const lineWidthAdjustment = (this.LINE_WIDTH / 2) * this.spaceViewRenderer.correctionMultiplier.current;
			adjustmentDistance += lineWidthAdjustment;
		}

		const adjustments = {
			[LinkDirection.BtoA]: {start: adjustmentDistance, end: 0},
			[LinkDirection.AtoB]: {start: 0, end: adjustmentDistance},
			[LinkDirection.Both]: {start: adjustmentDistance, end: adjustmentDistance},
		};

		const adjustment = adjustments[this._linkProperties.direction] || {start: 0, end: 0};

		return {
			adjustedStart: start.clone().add(directionVector.clone().multiplyScalar(adjustment.start)),
			adjustedEnd: end.clone().sub(directionVector.clone().multiplyScalar(adjustment.end)),
		};
	}

	private updateArrowHeads(start: Vector3, end: Vector3): void {
		if (!this.shouldCreateArrowHeads() || this._linkProperties.direction === LinkDirection.None) {
			this.cleanupArrowHeads();
			return;
		}

		const {objectAPosition, objectBPosition} = this.calculateArrowPositions(start, end);
		this._arrowTip1Position = objectBPosition.clone();
		this._arrowTip2Position = objectAPosition.clone();

		const arrowHeadPoints = this.calculateArrowHeadGeometry(objectAPosition, objectBPosition);
		this.updateArrowHeadsByDirection(objectAPosition, objectBPosition, arrowHeadPoints);
		this.updateLine(start, end);
	}

	private cleanupArrowHeads(): void {
		[this._arrowHead1, this._arrowHead2].forEach((arrowHead, index) => {
			if (arrowHead) {
				this.disposeArrowHead(arrowHead);
				if (index === 0) {
					this._arrowHead1 = undefined;
				} else {
					this._arrowHead2 = undefined;
				}
			}
		});
		this._spaceViewRenderer.needsRender = true;
	}

	private calculateArrowPositions(start: Vector3, end: Vector3): {objectAPosition: Vector3; objectBPosition: Vector3} {
		const objectAPosition = this._fromXyicon.id === this._link.objectAID ? start : end;
		const objectBPosition = this._fromXyicon.id === this._link.objectAID ? end : start;
		objectAPosition.z = objectBPosition.z = 0;
		return {objectAPosition, objectBPosition};
	}

	private calculateArrowHeadGeometry(objectAPosition: Vector3, objectBPosition: Vector3) {
		return calculateArrowHead(
			{x: objectAPosition.x, y: objectAPosition.y},
			{x: objectBPosition.x, y: objectBPosition.y},
			LinkLineArrow.ARROW_HEAD_WIDTH,
			this.spaceViewRenderer.correctionMultiplier.current,
		);
	}

	private updateArrowHeadsByDirection(objectAPosition: Vector3, objectBPosition: Vector3, arrowHeadPoints: any): void {
		const updates = [
			{
				condition: this._linkProperties.direction === LinkDirection.AtoB || this._linkProperties.direction === LinkDirection.Both,
				arrowHead: this._arrowHead1,
				position: objectBPosition,
				isAtoB: true,
				key: "_arrowHead1" as const,
			},
			{
				condition: this._linkProperties.direction === LinkDirection.BtoA || this._linkProperties.direction === LinkDirection.Both,
				arrowHead: this._arrowHead2,
				position: objectAPosition,
				isAtoB: false,
				key: "_arrowHead2" as const,
			},
		];

		updates.forEach(({condition, arrowHead, position, isAtoB, key}) => {
			if (condition && arrowHead) {
				this.updateSingleArrowHead(arrowHead, position, arrowHeadPoints, isAtoB, key);
			}
		});
	}

	private updateSingleArrowHead(
		arrowHead: Mesh | Line,
		position: Vector3,
		arrowHeadPoints: any,
		isAtoB: boolean,
		key: "_arrowHead1" | "_arrowHead2",
	): void {
		const geometry = new BufferGeometry();

		switch (this._linkProperties.settings.arrowStyle) {
			case ArrowStyle.Filled:
				this.createFilledArrowHead(arrowHead as Mesh, geometry, position, arrowHeadPoints, isAtoB);
				break;
			case ArrowStyle.Arrow:
				this[key] = this.createOutlineArrowHead(position, arrowHeadPoints, isAtoB);
				break;
			case ArrowStyle.Dotted:
				this.createDottedArrowHead(arrowHead as Mesh, geometry, position, isAtoB);
				break;
		}
	}

	private createFilledArrowHead(mesh: Mesh, geometry: BufferGeometry, position: Vector3, arrowHeadPoints: any, isAtoB: boolean): void {
		const {bc, bd} = arrowHeadPoints;
		const sign = isAtoB ? 1 : -1;
		const epsilon = Constants.EPSILON * this.spaceViewRenderer.correctionMultiplier.current;

		const positions = new Float32Array([
			position.x,
			position.y,
			epsilon,
			position.x + sign * bc.x,
			position.y + sign * bc.y,
			epsilon,
			position.x + sign * bd.x,
			position.y + sign * bd.y,
			epsilon,
		]);

		geometry.setAttribute("position", new BufferAttribute(positions, 3));
		geometry.setIndex([0, 1, 2]);
		geometry.computeVertexNormals();
		mesh.geometry = geometry;

		this.ensureArrowHeadInContainer(mesh);
	}

	private createOutlineArrowHead(position: Vector3, arrowHeadPoints: any, isAtoB: boolean): Line2 {
		const {bc, bd} = arrowHeadPoints;
		const sign = isAtoB ? 1 : -1;
		const epsilon = Constants.EPSILON * this.spaceViewRenderer.correctionMultiplier.current;

		const arrowLineGeometry = new LineGeometry();
		arrowLineGeometry.setPositions([
			position.x,
			position.y,
			epsilon,
			position.x + sign * bc.x,
			position.y + sign * bc.y,
			epsilon,
			position.x,
			position.y,
			epsilon,
			position.x + sign * bd.x,
			position.y + sign * bd.y,
			epsilon,
		]);

		const arrowLineMaterial = this.createArrowLineMaterial();
		const newArrowHead = new Line2(arrowLineGeometry, arrowLineMaterial);
		newArrowHead.name = "linkLine";

		if (arrowLineMaterial.dashed) {
			newArrowHead.computeLineDistances();
		}

		THREEUtils.add(this._floorContainer, newArrowHead);
		THREEUtils.updateMatrices(this._floorContainer);

		return newArrowHead;
	}

	private createDottedArrowHead(mesh: Mesh, geometry: BufferGeometry, position: Vector3, isAtoB: boolean): void {
		const radius = this.ARROW_HEAD_WIDTH / 2;
		const circleGeometry = new CircleGeometry(radius, 32);

		const direction = isAtoB
			? new Vector3().subVectors(this._arrowTip1Position!, this._arrowTip2Position!).normalize()
			: new Vector3().subVectors(this._arrowTip2Position!, this._arrowTip1Position!).normalize();

		const circleCenter = position.clone().add(direction.clone().multiplyScalar(-radius));
		THREEUtils.applyOffsetToBufferGeometry(circleGeometry, circleCenter);

		geometry.copy(circleGeometry);
		mesh.geometry = geometry;
		this.ensureArrowHeadInContainer(mesh);
	}

	private ensureArrowHeadInContainer(mesh: Mesh): void {
		if (!this._floorContainer.children.includes(mesh)) {
			THREEUtils.add(this._floorContainer, mesh);
			THREEUtils.updateMatrices(this._floorContainer);
		}
		this._spaceViewRenderer.needsRender = true;
	}

	// Selection box methods
	private createSelectionBox(): Group {
		const group = new Group();
		group.name = "linkLineSelectionBoxGroup";

		const boxes = [
			{size: this.SELECTION_BOX_SIZE, color: "#1E88E5", name: "linkLineSelectionBox", zOffset: 0},
			{
				size: this.INNER_BOX_SIZE,
				color: "#ffffff",
				name: "linkLineInnerSelectionBox",
				zOffset: this.SELECTION_BOX_SIZE / 2 + this.INNER_BOX_SIZE / 2,
			},
		];

		boxes.forEach(({size, color, name, zOffset}) => {
			const geometry = this.createRoundedBoxGeometry(size, size, size, this.SELECTION_BOX_RADIUS);
			const material = new MeshBasicMaterial({color, side: DoubleSide, transparent: true, opacity: 1});
			const box = new Mesh(geometry, material);
			box.name = name;
			box.position.set(0, 0, zOffset);
			group.add(box);
		});

		return group;
	}

	private createRoundedBoxGeometry(width: number, height: number, depth: number, radius: number): BufferGeometry {
		const shape = new Shape();
		const x = width / 2,
			y = height / 2;

		shape.moveTo(-x + radius, -y);
		shape.lineTo(x - radius, -y);
		shape.quadraticCurveTo(x, -y, x, -y + radius);
		shape.lineTo(x, y - radius);
		shape.quadraticCurveTo(x, y, x - radius, y);
		shape.lineTo(-x + radius, y);
		shape.quadraticCurveTo(-x, y, -x, y - radius);
		shape.lineTo(-x, -y + radius);
		shape.quadraticCurveTo(-x, -y, -x + radius, -y);

		return new ExtrudeGeometry(shape, {
			depth,
			bevelEnabled: true,
			bevelThickness: radius * 0.5,
			bevelSize: radius * 0.5,
			bevelOffset: 0,
			bevelSegments: 4,
		});
	}

	private showSelectionBoxes(): void {
		const lineDirection = this.getArrowLineDirection();
		const epsilon = Constants.EPSILON * this.spaceViewRenderer.correctionMultiplier.current;

		if (this._linkProperties.direction === LinkDirection.None) {
			this.showSelectionBoxesForNoneDirection(lineDirection, epsilon);
			return;
		}

		if (!this.shouldCreateArrowHeads()) {
			return;
		}
		this.showSelectionBoxesForDirection(lineDirection, epsilon);
	}

	// Update the showSelectionBoxesForNoneDirection method
	private showSelectionBoxesForNoneDirection(lineDirection: {angle: number}, epsilon: number): void {
		if (!this._originalIntersectionA || !this._originalIntersectionB) {
			return;
		}

		const positions = [this._originalIntersectionA, this._originalIntersectionB];

		[this._selectionBox1, this._selectionBox2].forEach((box, index) => {
			if (!box) {
				if (index === 0) {
					this._selectionBox1 = this.createSelectionBox();
				} else {
					this._selectionBox2 = this.createSelectionBox();
				}
				box = index === 0 ? this._selectionBox1! : this._selectionBox2!;
			}

			// Use the specialized method for None direction
			const position = this.calculatePerpendicularBoxPositionForNone(positions[index], index === 0);

			box.position.set(position.x, position.y, epsilon);
			box.rotation.z = lineDirection.angle;
			THREEUtils.add(this._floorContainer, box);
		});

		this._spaceViewRenderer.needsRender = true;
	}

	private showSelectionBoxesForDirection(lineDirection: {angle: number}, epsilon: number): void {
		const arrowInfos: ArrowHeadInfo[] = [
			{
				arrowHead: this._arrowHead1,
				tipPosition: this._arrowTip1Position,
				isAtoB: true,
				propertyKey: "_arrowHead1",
				selectionBoxKey: "_selectionBox1",
			},
			{
				arrowHead: this._arrowHead2,
				tipPosition: this._arrowTip2Position,
				isAtoB: false,
				propertyKey: "_arrowHead2",
				selectionBoxKey: "_selectionBox2",
			},
		];

		arrowInfos.forEach((info) => {
			const shouldShow = this.shouldShowSelectionBoxForDirection(info);
			if (shouldShow && info.tipPosition) {
				this.showSingleSelectionBox(info, lineDirection, epsilon);
			}
		});

		this._spaceViewRenderer.needsRender = true;
	}

	private shouldShowSelectionBoxForDirection(info: ArrowHeadInfo): boolean {
		const {arrowHead, isAtoB} = info;
		const direction = this._linkProperties.direction;

		return (
			(direction === LinkDirection.AtoB && (!!arrowHead || !isAtoB)) ||
			(direction === LinkDirection.BtoA && (!!arrowHead || isAtoB)) ||
			(direction === LinkDirection.Both && !!arrowHead)
		);
	}

	private showSingleSelectionBox(info: ArrowHeadInfo, lineDirection: {angle: number}, epsilon: number): void {
		if (!this[info.selectionBoxKey]) {
			this[info.selectionBoxKey] = this.createSelectionBox();
		}

		const boxPosition =
			info.arrowHead && this._linkProperties.settings.arrowStyle === ArrowStyle.Dotted
				? this.calculateDottedArrowCenterPosition(info.tipPosition!, info.isAtoB)
				: this.calculatePerpendicularBoxPosition(info.tipPosition!);

		const selectionBox = this[info.selectionBoxKey]!;
		selectionBox.position.set(boxPosition.x, boxPosition.y, epsilon);
		selectionBox.rotation.z = lineDirection.angle;
		THREEUtils.add(this._floorContainer, selectionBox);
	}

	private hideSelectionBoxes(): void {
		[this._selectionBox1, this._selectionBox2].forEach((box, index) => {
			if (box) {
				this._floorContainer.remove(box);
				this.disposeSelectionBoxGroup(box);
				if (index === 0) {
					this._selectionBox1 = undefined;
				} else {
					this._selectionBox2 = undefined;
				}
			}
		});
		this._spaceViewRenderer.needsRender = true;
	}

	private disposeSelectionBoxGroup(group: Group): void {
		group.children.forEach((child) => {
			if (child instanceof Mesh) {
				child.geometry.dispose();
				if (child.material instanceof MeshBasicMaterial) {
					child.material.dispose();
				}
			}
		});
		group.clear();
	}

	private getArrowLineDirection(): {angle: number; direction: Vector3} {
		const objectACenter = new Vector3(this._fromXyicon.position.x, this._fromXyicon.position.y, 0);
		const objectBCenter = new Vector3(this._toXyicon.position.x, this._toXyicon.position.y, 0);
		const lineDirection = new Vector3().subVectors(objectBCenter, objectACenter).normalize();
		const angle = Math.atan2(lineDirection.y, lineDirection.x) + Math.PI / 2;
		return {angle, direction: lineDirection};
	}

	// Add this new method for handling None direction cases
	private calculatePerpendicularBoxPositionForNone(lineEndPosition: Vector3, isStartPosition: boolean): Vector3 {
		const {direction: lineDirection} = this.getArrowLineDirection();
		const halfBoxLength = this.SELECTION_BOX_SIZE / 2;
		// For None direction, move the box inward from the line endpoints
		const moveDirection = isStartPosition ? lineDirection.clone() : lineDirection.clone().negate();
		return lineEndPosition.clone().add(moveDirection.multiplyScalar(halfBoxLength));
	}

	// Update the existing method to handle undefined arrow tip positions
	private calculatePerpendicularBoxPosition(arrowTipPosition: Vector3): Vector3 {
		// Check if arrow tip positions are defined (they won't be for LinkDirection.None)
		if (!this._arrowTip1Position || !this._arrowTip2Position) {
			// Fallback: treat the position as a line endpoint and move inward
			return this.calculatePerpendicularBoxPositionForNone(arrowTipPosition, true);
		}

		const {direction: lineDirection} = this.getArrowLineDirection();
		const halfBoxLength = this.SELECTION_BOX_SIZE / 2;
		const isAtObjectB = arrowTipPosition.equals(this._arrowTip1Position);
		const moveDirection = isAtObjectB ? lineDirection.clone().negate() : lineDirection.clone();
		return arrowTipPosition.clone().add(moveDirection.multiplyScalar(halfBoxLength));
	}

	private calculateDottedArrowCenterPosition(arrowTipPosition: Vector3, isArrowHead1: boolean): Vector3 {
		const radius = this.ARROW_HEAD_WIDTH / 2;
		const sourcePos = isArrowHead1 ? this._arrowTip2Position! : this._arrowTip1Position!;
		const targetPos = isArrowHead1 ? this._arrowTip1Position! : this._arrowTip2Position!;
		const direction = new Vector3().subVectors(targetPos, sourcePos).normalize();
		return arrowTipPosition.clone().add(direction.clone().multiplyScalar(-radius));
	}

	// Utility methods for disposal and mesh management
	private disposeArrowHead(arrowHead: Mesh | Line): void {
		this._floorContainer.remove(arrowHead);
		arrowHead.geometry.dispose();
		if (arrowHead.material) {
			if (Array.isArray(arrowHead.material)) {
				arrowHead.material.forEach((mat) => mat.dispose());
			} else {
				arrowHead.material.dispose();
			}
		}
	}

	private updateArrowHeadMaterials(): void {
		[this._arrowHead1, this._arrowHead2].forEach((arrowHead) => {
			if (arrowHead?.material && (arrowHead.material instanceof LineMaterial || arrowHead.material instanceof MeshBasicMaterial)) {
				arrowHead.material.color.setHex(this._lineColor);
			}
		});
	}

	// Event handlers
	public override mouseOver() {
		if (!this._isSelected && !this._isHovered) {
			this._isHovered = true;
			this.setColor(this._color, SpaceItem.COLOR_INTENSITY.HOVERED);
			this._spaceViewRenderer.needsRender = true;
		}
	}

	public override mouseOut() {
		if (!this._isSelected && this._isHovered) {
			this._isHovered = false;
			this.setColor(this._color, SpaceItem.COLOR_INTENSITY.DESELECTED);
			this._spaceViewRenderer.needsRender = true;
		}
	}

	public override deselect(updateSelectionBox: boolean = true): boolean {
		if (this._isSelected) {
			this.hideSelectionBoxes();
			this._link.isSelected = false;
			return super.deselect(updateSelectionBox);
		}
		return false;
	}

	public deselectArrowHeads() {
		if (this._isSelected) {
			this._isSelected = false;
			this._link.isSelected = false;
			this.hideSelectionBoxes();
			const intensity = this._isHovered ? SpaceItem.COLOR_INTENSITY.HOVERED : SpaceItem.COLOR_INTENSITY.DESELECTED;
			this.setColor(this._color, intensity);
			this._spaceViewRenderer.needsRender = true;
		}
	}

	public selectLinkPosition() {
		this._spaceViewRenderer.spaceItemController.deselectAll(false, false, false, false);

		if (!this._isSelected) {
			this._spaceViewRenderer.spaceItemController.linkLineManager.deselectAll();
			this._isSelected = true;
			this._link.isSelected = true;
			this.showSelectionBoxes();
			this._spaceViewRenderer.needsRender = true;
		}

		if (this._spaceViewRenderer.inheritedMethods.getFocusedItems().length > 0) {
			this._spaceViewRenderer.inheritedMethods.focusItems([this._link as any]);
		}
	}

	public dispose(): void {
		this._lineGeometry.dispose();
		this._material.dispose();
		this.hideSelectionBoxes();
		[this._arrowHead1, this._arrowHead2].forEach((arrowHead) => {
			if (arrowHead) {
				this.disposeArrowHead(arrowHead);
			}
		});
		this._floorContainer.remove(this._line);
	}

	public containsMesh(mesh: Object3D): boolean {
		return (
			mesh === this._line ||
			mesh === this._arrowHead1 ||
			mesh === this._arrowHead2 ||
			this.isInSelectionBox(mesh, this._selectionBox1) ||
			this.isInSelectionBox(mesh, this._selectionBox2)
		);
	}

	private isInSelectionBox(mesh: Object3D, selectionBox: Group | undefined): boolean {
		return !!selectionBox && (mesh === selectionBox || selectionBox.children.includes(mesh));
	}

	public setDirection(direction: LinkDirection): void {
		this._linkProperties.direction = direction;
		this.hideSelectionBoxes();
		this.cleanupArrowHeads();

		if (direction === LinkDirection.None) {
			this.updateLineWithOriginalPoints();
			return;
		}

		if (this.shouldCreateArrowHeads()) {
			this.createArrowHeadsForDirection(direction);
		}
		this.updateArrowsAndLine();
	}

	private updateLineWithOriginalPoints(): void {
		this.updateLine(this._originalIntersectionA!.clone(), this._originalIntersectionB!.clone());
	}

	private createArrowHeadsForDirection(direction: LinkDirection): void {
		const directions = [
			{condition: direction === LinkDirection.AtoB || direction === LinkDirection.Both, key: "_arrowHead1" as const},
			{condition: direction === LinkDirection.BtoA || direction === LinkDirection.Both, key: "_arrowHead2" as const},
		];

		directions.forEach(({condition, key}) => {
			if (condition) {
				this[key] = this.createBasicMeshArrowHead();
				THREEUtils.add(this._floorContainer, this[key]!);
			}
		});
	}

	private updateArrowsAndLine(): void {
		const start = this._originalIntersectionA!.clone();
		const end = this._originalIntersectionB!.clone();
		this.updateLine(start, end);
		this.updateArrowHeads(start, end);
		THREEUtils.updateMatrices(this._floorContainer);
		if (this._isSelected) {
			this.showSelectionBoxes();
		}
		this._spaceViewRenderer.needsRender = true;
	}

	public updateLinkProperties(linkProperties: LinkPropertyDto): void {
		const oldArrowStyle = this._linkProperties.settings.arrowStyle;
		const oldDirection = this._linkProperties.direction;

		this.hideSelectionBoxes();
		this._linkProperties = this.createDefaultLinkProperties({
			...this._linkProperties,
			...linkProperties,
			settings: {...this._linkProperties.settings, ...linkProperties.settings},
		});

		this.updateVisualProperties();

		if (oldArrowStyle !== this._linkProperties.settings.arrowStyle || oldDirection !== this._linkProperties.direction) {
			this.recreateArrowHeads();
		}

		THREEUtils.updateMatrices(this._floorContainer);
		this._spaceViewRenderer.needsRender = true;
	}

	private updateVisualProperties(): void {
		this._lineColor = this.getLineColorAsNumber(this._linkProperties.settings.lineColor);
		this._line.material.color.setHex(this._lineColor);
		this._line.material.linewidth = this._linkProperties.settings.lineWidth;
		this.applyDashedLineStyle();
		this.updateArrowHeadMaterials();
	}

	private recreateArrowHeads(): void {
		this.cleanupArrowHeads();
		this.updateArrowsAndLine();
	}

	public getIntersectables(): Object3D[] {
		return [this._line, this._arrowHead1, this._arrowHead2].filter(Boolean) as Object3D[];
	}

	public onCanvasResize() {
		this._material.resolution.set(this._spaceViewRenderer.canvas.width, this._spaceViewRenderer.canvas.height);
	}

	// Abstract getters and required methods
	public get floorContainer(): Group {
		return this._floorContainer;
	}
	public get link(): Link {
		return this._link;
	}
	public get direction(): LinkDirection {
		return this._linkProperties?.direction;
	}
	public get type(): number | string {
		return "link";
	}
	public override get orientation(): number {
		return 0;
	}
	public get itemManager(): any {
		return this._spaceViewRenderer.spaceItemController.linkLineManager;
	}

	public get position(): Dimensions {
		const fromPos = this._fromXyicon.position;
		const toPos = this._toXyicon.position;
		return {x: (fromPos.x + toPos.x) / 2, y: (fromPos.y + toPos.y) / 2, z: 0};
	}

	public get scale(): PointDouble {
		return {x: 1, y: 1};
	}

	public get boundingBox(): IBox2 {
		const fromPos = this._fromXyicon.position;
		const toPos = this._toXyicon.position;
		return {
			min: {x: Math.min(fromPos.x, toPos.x), y: Math.min(fromPos.y, toPos.y)},
			max: {x: Math.max(fromPos.x, toPos.x), y: Math.max(fromPos.y, toPos.y)},
		};
	}

	// Abstract methods from SpaceItem
	protected destroyCallback(notifyServer?: boolean, removeFromCollections?: boolean): void {
		this.dispose();
	}
	public rotateWithHandlerByDelta(deltaAngle: number, pivot?: PointDouble): void {
		/* Link lines don't rotate */
	}
	public stopRotating(): void {
		/* Link lines don't rotate */
	}
	public updateByModel(model: IModel): void {
		this._modelData = model;
	}
	protected applyOrientation(newOrientation: number): void {
		/* Link lines don't have orientation */
	}
	protected setFormattingColor(colorRuleCategory: any, colorHex: string): void {
		/* Implementation if needed */
	}
	protected setGrayScaled(value: boolean): void {
		/* Implementation if needed */
	}

	protected override setColor(color: number, intensity: number, save?: boolean): void {
		this._material.color.setHex(color).multiplyScalar(intensity);
		[this._arrowHead1, this._arrowHead2].forEach((arrowHead) => {
			if (arrowHead?.material && (arrowHead.material instanceof LineMaterial || arrowHead.material instanceof MeshBasicMaterial)) {
				arrowHead.material.color.setHex(color).multiplyScalar(intensity);
			}
		});
		if (save) {
			this._lineColor = color;
		}
	}
}
